import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { Zamestnanec, ZamestnanecInput } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { usePermissions } from '@/lib/permissions';

const QUERY_KEY = ['zamestnanci'] as const;
const ENDPOINT = '/zamestnanci';

/** Tooltip na zakazanych tlacitkach — at je z UI poznat proc jsou vypnuta. */
const BEZ_OPRAVNENI = 'Nemáte oprávnění spravovat číselníky (vyžaduje roli Vedoucí nebo Administrátor).';

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; item: Zamestnanec }
  | { mode: 'delete'; item: Zamestnanec };

export function ZamestnanciPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canSpravovatCiselniky } = usePermissions();
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });

  const listQuery = useQuery<Zamestnanec[]>({
    queryKey: QUERY_KEY,
    queryFn: () => api<Zamestnanec[]>(ENDPOINT),
  });

  const createMutation = useMutation({
    mutationFn: (input: ZamestnanecInput) =>
      api<Zamestnanec>(ENDPOINT, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Zaměstnanec přidán' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Přidání selhalo', description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ZamestnanecInput }) =>
      api<Zamestnanec>(`${ENDPOINT}/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Zaměstnanec upraven' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Úprava selhala', description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api<void>(`${ENDPOINT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Zaměstnanec deaktivován' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Deaktivace selhala', description: err.message, variant: 'destructive' }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Zaměstnanci</h2>
          <p className="text-sm text-muted-foreground">
            Číselník osob nabízených u položek operativy
          </p>
        </div>
        <Button
          onClick={() => setDialog({ mode: 'create' })}
          disabled={!canSpravovatCiselniky}
          title={canSpravovatCiselniky ? undefined : BEZ_OPRAVNENI}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nový zaměstnanec
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Seznam zaměstnanců</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : listQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(listQuery.error as Error).message}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jméno</TableHead>
                    <TableHead className="w-28">Aktivní</TableHead>
                    <TableHead className="w-40 text-right">Akce</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(listQuery.data ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                        Zatím žádné záznamy. Zaměstnance lze přidat i přímo z formuláře položky
                        operativy.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (listQuery.data ?? []).map((z) => (
                      <TableRow key={z.id}>
                        <TableCell>{z.jmeno}</TableCell>
                        <TableCell>
                          {z.aktivni ? (
                            <Badge variant="success">Ano</Badge>
                          ) : (
                            <Badge variant="secondary">Ne</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!canSpravovatCiselniky}
                            title={canSpravovatCiselniky ? 'Upravit' : BEZ_OPRAVNENI}
                            onClick={() => setDialog({ mode: 'edit', item: z })}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!canSpravovatCiselniky}
                            title={canSpravovatCiselniky ? 'Deaktivovat' : BEZ_OPRAVNENI}
                            onClick={() => setDialog({ mode: 'delete', item: z })}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ZamestnanecFormDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onSubmit={(input, id) => {
          if (id) updateMutation.mutate({ id, input });
          else createMutation.mutate(input);
        }}
        submitting={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDeleteDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onConfirm={(id) => deleteMutation.mutate(id)}
        submitting={deleteMutation.isPending}
      />
    </div>
  );
}

interface FormDialogProps {
  state: DialogState;
  onClose: () => void;
  onSubmit: (input: ZamestnanecInput, id?: string) => void;
  submitting: boolean;
}

function ZamestnanecFormDialog({ state, onClose, onSubmit, submitting }: FormDialogProps) {
  const open = state.mode === 'create' || state.mode === 'edit';
  const editItem = state.mode === 'edit' ? state.item : null;

  const [jmeno, setJmeno] = useState('');
  const [aktivni, setAktivni] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setJmeno(editItem.jmeno);
      setAktivni(editItem.aktivni);
    } else {
      setJmeno('');
      setAktivni(true);
    }
  }, [open, editItem]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ jmeno: jmeno.trim(), aktivni }, editItem?.id);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editItem ? 'Upravit zaměstnance' : 'Nový zaměstnanec'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="jmeno">Jméno</Label>
            <Input
              id="jmeno"
              required
              value={jmeno}
              onChange={(e) => setJmeno(e.target.value)}
              placeholder="Novák Jan"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="aktivni"
              type="checkbox"
              checked={aktivni}
              onChange={(e) => setAktivni(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="aktivni">Aktivní</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={submitting || jmeno.trim() === ''}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ConfirmProps {
  state: DialogState;
  onClose: () => void;
  onConfirm: (id: string) => void;
  submitting: boolean;
}

function ConfirmDeleteDialog({ state, onClose, onConfirm, submitting }: ConfirmProps) {
  const open = state.mode === 'delete';
  const item = state.mode === 'delete' ? state.item : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deaktivovat zaměstnance?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Opravdu chcete deaktivovat <strong>{item?.jmeno}</strong>? Přestane se nabízet
          u nových položek; u existujících položek jméno zůstane.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={() => item && onConfirm(item.id)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Deaktivovat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
